import React, { useState } from 'react';

import { CardLink } from 'components/custom-icons';
import { CheckboxField } from 'components/fields/TextField';
import { Modal } from 'components/modal/Modal';
import { firebaseValueAsString, useFirebaseConfigValues } from 'hooks/useFirebaseConfigValues';
import { createStyles } from 'utils/createStyle';
import { Btn } from 'components/btn/Btn';
import { isInIframe, mainWebsiteOrigin } from 'utils';

export const CardLinkingStatusAndButton = ({
    isCheckboxDisabled,
    unEnrollFromRewardsNetwork,
    enrolledInRewardsNetwork,
    onClick,
}: {
    isCheckboxDisabled: boolean;
    unEnrollFromRewardsNetwork: () => void;
    enrolledInRewardsNetwork: boolean;
    onClick: () => void;
}) => {
    const toggleEnrollCard = () => {
        unEnrollFromRewardsNetwork();
    };

    // NOTE: This component is used in checkout _only_ for now.
    // If we want to have it be used elsewhere we'll need to remove the
    // `logViewedCheckout` and put it in the `onClick`.
    const onClickLinkWithClo = () => {
        if (isInIframe()) {
            window.parent.postMessage(
                {
                    type: 'view_checkout',
                    action: 'clicked_link_card',
                },
                mainWebsiteOrigin()
            );
        }
        onClick();
    };

    return (
        <div>
            {enrolledInRewardsNetwork ? (
                <CheckboxField
                    label={
                        <>
                            <span className="text-orange text-sm pr-1 font-semibold">New!</span>{' '}
                            <span className="text-sm text-grey-darkest">
                                Link to earn with local dining
                            </span>
                        </>
                    }
                    onStateChange={() => toggleEnrollCard()}
                    checked
                    disabled={isCheckboxDisabled}
                />
            ) : (
                <button
                    role="button"
                    type="button"
                    onClick={onClickLinkWithClo}
                    className="text-sm bg-pale-blue px-4 py-2 rounded-lg mt-1"
                >
                    <span className="text-orange text-sm font-semibold">New!</span>{' '}
                    <span className="underline text-sm text-brand font-semibold">
                        Link to earn with local dining
                    </span>
                </button>
            )}
        </div>
    );
};

const modalStyles = createStyles({
    checkboxErrorWrapper: {
        '& button[role="checkbox"]': {
            borderColor: 'red !important',
        },
    },
    buttonNoBorder: {
        '& button': {
            border: 'none',
            textDecoration: 'underline',
        },
    },
    headerText: [
        'pb-2 text-brand-dark font-semibold text-24px',
        {
            paddingTop: '8px',
            lineHeight: '32px',
        },
    ],
    subHeaderText: [
        'text-grey-darkest pb-2 text-16px',
        {
            lineHeight: '24px',
            paddingBottom: '40px',
        },
    ],
    bodyText: [
        'pt-2 content-start text-left text-grey-darkest font-light text-14px',
        {
            lineHeight: '20px',
        },
    ],
});

type CardLinkingTermsModalProps = {
    isVisible: boolean;
    onClose: () => void;
    onLinkACard: () => void;
};

export const CardLinkingTermsModal = ({
    isVisible,
    onClose,
    onLinkACard,
}: CardLinkingTermsModalProps) => {
    const [showValidationWarnings, setShowValidationWarnings] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);

    const onClose_ = () => {
        setTermsAccepted(false);
        setShowValidationWarnings(false);
        onClose();
    };

    const _onTermsAccepted = () => {
        if (!termsAccepted) {
            setShowValidationWarnings(true);
            return;
        }

        onClose_();
        onLinkACard();
    };

    const renderWarnings = showValidationWarnings && !termsAccepted;
    const earnings = useFirebaseConfigValues('local_dining_earnings', '5', firebaseValueAsString);

    return (
        <Modal isOpen={isVisible} title={undefined} onClose={onClose_}>
            <div className="flex flex-col align-center center text-center">
                <div className="flex flex-row items-center justify-center">
                    <CardLink />
                </div>

                <p className="pt-4 italic text-brand-dark font-regular text-16px">
                    You're almost there!
                </p>

                <h2 className={modalStyles.headerText}>
                    Earn {earnings}% at participating local restaurants when you pay with a linked
                    card
                </h2>

                <h4 className={modalStyles.subHeaderText}>
                    To find restaurants open the RaiseRight app and navigate to Local Dining from
                    the home screen.
                </h4>

                <span className={renderWarnings ? modalStyles.checkboxErrorWrapper : ''}>
                    <CheckboxField
                        label={
                            <span className="text-grey-darkest font-semibold text-14px pl-1">
                                Accept Terms &amp; Conditions
                            </span>
                        }
                        onStateChange={(next) => setTermsAccepted(next)}
                        checked={termsAccepted}
                    />
                </span>

                <p className={modalStyles.bodyText}>
                    I authorize payment networks (e.g. American Express, Mastercard, and Visa) to
                    monitor activity on my linked payment card(s) to identify qualifying
                    transactions, and for such payment networks to share transaction details with
                    Rewards Network to enable my card-linked offer, present me with targeted offers
                    that may be of interest to me, and otherwise facilitate my participation in
                    RaiseRight’s local dining program as set forth in the Terms of Use and Privacy
                    Policy. I understand I may opt out of transaction monitoring on my linked
                    payment card(s) by removing my card at any time by following the instructions
                    provided within the Help Center.
                </p>

                {renderWarnings && (
                    <div className="text-error content-start text-left font-normal text-14px">
                        Please select the ‘Accept Terms & Conditions’ checkbox above to continue.
                    </div>
                )}

                <div className="flex flex-row gap-2 items-center justify-center">
                    <div className="pb-4 pt-5">
                        <Btn type="button" onClick={_onTermsAccepted}>
                            Link Card
                        </Btn>
                    </div>
                    <div className={modalStyles.buttonNoBorder}>
                        <Btn type="button" isSecondary onClick={onClose}>
                            Cancel
                        </Btn>
                    </div>
                </div>
            </div>
        </Modal>
    );
};
